import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { projects } from "../mock";
import { playTick } from "../lib/tick";

const onTileClick = () => playTick();

// One distinct hue per project — keyed by slug so reordering the array
// never re-shuffles the colors.
const palettes = {
  "500m-divers":          { bg: "#1A2138", fg: "#FAFAF7", muted: "rgba(250,250,247,0.55)", line: "rgba(250,250,247,0.25)" },
  "stellar-1":            { bg: "#0B0B0B", fg: "#F4F1E8", muted: "rgba(244,241,232,0.55)", line: "rgba(244,241,232,0.25)" },
  "300m-divers":          { bg: "#3A5570", fg: "#F4E8D6", muted: "rgba(244,232,214,0.6)",  line: "rgba(244,232,214,0.28)" },
  "squircle":             { bg: "#1F2A24", fg: "#E8E2CF", muted: "rgba(232,226,207,0.6)",  line: "rgba(232,226,207,0.28)" },
  "stellar-2-luna":       { bg: "#E0D4B7", fg: "#1A1410", muted: "rgba(26,20,16,0.6)",     line: "rgba(26,20,16,0.25)" },
  "stellar-3-micromotor": { bg: "#3A2D22", fg: "#E8DCC4", muted: "rgba(232,220,196,0.6)",  line: "rgba(232,220,196,0.28)" }
};

const DEFAULT_PALETTE = { bg: "#0B0B0B", fg: "#F4F1E8", muted: "rgba(244,241,232,0.55)", line: "rgba(244,241,232,0.25)" };

const SelectedWork = () => {
  return (
    <section id="work">
      <div className="w-full">
        {projects.map((p, idx) => {
          const c = palettes[p.slug] || DEFAULT_PALETTE;
          return (
            <article
              key={p.id}
              className="grid grid-cols-1 md:grid-cols-2 gap-0 reveal snap-project h-screen md:h-auto ipad-portrait-fill"
            >
              {/* LEFT — image (clickable) */}
              <Link
                to={`/work/${p.slug}`}
                onClick={onTileClick}
                className="group block h-[45vh] md:h-auto md:aspect-[4/3] overflow-hidden bg-[#EFEDE6] relative"
                aria-label={`View ${p.title}`}
              >
                <img
                  src={p.cover}
                  alt={p.title}
                  loading={idx < 2 ? "eager" : "lazy"}
                  style={{ objectPosition: p.coverPosition || "center" }}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                />
              </Link>

              {/* RIGHT — description square (color varies per project) */}
              <div
                className="h-[55vh] md:h-auto md:aspect-[4/3] px-6 sm:px-8 md:px-10 lg:px-14 py-6 sm:py-8 md:py-10 lg:py-14 flex flex-col justify-between ipad-portrait-129"
                style={{ backgroundColor: c.bg, color: c.fg }}
              >
                <div>
                  <div
                    className="mono uppercase tracking-widest text-[11px] md:text-[11px] lg:text-[15px] tile-client"
                    style={{ color: c.muted }}
                  >
                    {p.client}
                  </div>

                  <Link to={`/work/${p.slug}`} onClick={onTileClick} className="inline-block">
                    <h3 className="sohne tracking-tight leading-[0.98] mt-3 md:mt-4 lg:mt-7 text-3xl sm:text-4xl md:text-3xl lg:text-5xl xl:text-6xl tile-title hover-line">
                      {p.title}
                    </h3>
                  </Link>

                  <p
                    className="mt-3 md:mt-3 lg:mt-6 leading-snug md:leading-snug lg:leading-relaxed text-[13px] sm:text-sm md:text-[12px] lg:text-base max-w-md font-light tile-summary"
                    style={{ color: c.muted }}
                  >
                    {p.tileSummary || p.summary}
                  </p>
                </div>

                <div className="flex items-end justify-between md:justify-end lg:justify-between gap-3 pt-4 md:pt-4 lg:pt-6 tile-arrow-row">
                  <div className="flex md:hidden lg:flex flex-wrap gap-1 lg:gap-1.5 ipad-portrait-hide-tags">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="mono uppercase tracking-widest px-1.5 py-0.5 md:px-1.5 md:py-0.5 lg:px-3 lg:py-1.5 rounded-sm text-[9px] md:text-[9px] lg:text-[13px]"
                        style={{
                          color: c.muted,
                          border: `1px solid ${c.line}`
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/work/${p.slug}`}
                    onClick={onTileClick}
                    aria-label={`View ${p.title}`}
                    className="group inline-flex items-center justify-center shrink-0"
                    style={{ color: c.fg }}
                  >
                    <ArrowUpRight
                      className="w-7 h-7 md:w-8 md:h-8 transition-transform duration-500 group-hover:rotate-45"
                      strokeWidth={1.25}
                    />
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default SelectedWork;
